import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import LoginApi from "../Api/LoginApi";
import { toast } from "react-toastify";

const SignInLayer = () => {
  const navigate = useNavigate();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [pin, setPin] = useState(["", "", "", ""]);
  const [showPin, setShowPin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({ phoneNumber: "", pin: "" });
  const pinRefs = useRef([]);

  const handlePinChange = (index, value) => {
    if (isNaN(value)) return;
    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);
    setErrors((prev) => ({ ...prev, pin: "" }));

    if (value && index < 3) {
      pinRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !pin[index] && index > 0) {
      pinRefs.current[index - 1].focus();
    }
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    let newErrors = {};

    if (!phoneNumber.trim()) {
      newErrors.phoneNumber = "Please Enter Your Phone Number";
    }
    const pinValue = pin.join("");
    if (pinValue.length !== 4) {
      newErrors.pin = "Please Enter a Complete 4-Digit PIN";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      const credentials = {
        phoneNumber: phoneNumber.trim(),
        pin: pinValue,
      };
      const result = await LoginApi.login(credentials);
      if (result.status) {
        const token =
          result.response.token ||
          result.response.data?.token ||
          result.response.accessToken;
        const user = result.response.user || result.response.data?.user;

        if (token) {
          localStorage.setItem("userToken", token);
        } else {
          console.error("Token missing in response:", result.response);
          toast.error("Login successful but token missing from response.");
          setLoading(false);
          return;
        }

        if (user) {
          localStorage.setItem("userData", JSON.stringify(user));
        }
        setTimeout(() => {
          navigate("/");
        }, 100);
      }
    } catch (error) {
      console.error("Login error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      className="auth min-vh-100 d-flex justify-content-center align-items-center bg-primary-600"

    >
      <style>
        {`
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          .login-card {
            animation: fadeInUp 0.6s ease-out;
          }
          .form-control:focus {
            border-color: #364E3D !important;
            box-shadow: 0 0 0 3px rgba(54, 78, 61, 0.1) !important;
            background-color: #fff !important;
          }
          input:-webkit-autofill,
          input:-webkit-autofill:hover, 
          input:-webkit-autofill:focus, 
          input:-webkit-autofill:active {
            -webkit-box-shadow: 0 0 0 30px #f5f6fa inset !important;
            -webkit-text-fill-color: #111827 !important;
          }
          .pin-input {
            border-width: 2px !important;
          }
        `}
      </style>

      <div
        className="login-card bg-white radius-16 p-32 p-sm-40 shadow-lg text-center mx-3"
        style={{ maxWidth: "450px", width: "100%" }}
      >
        <div className="mb-32">
          <Link to="/" className="d-inline-block mb-24">
            <img
              src="/assets/images/logo.png"
              alt="logo.png"
              style={{ maxHeight: "120px", width: "auto", objectFit: "contain" }}
            />
          </Link>
          <h4 className="mb-12 fw-bold text-dark">Sign In</h4>
          <p className="text-secondary-light text-md">
            Enter Your Phone Number and PIN to Continue
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="icon-field mb-32 text-start">
            <label className="form-label text-secondary text-sm fw-medium mb-8">
              Phone Number
            </label>
            <div className="position-relative">
              <span className="icon top-50 translate-middle-y start-0 auth-icon-left text-secondary-light">
                <Icon icon="mage:phone" className="text-xl" />
              </span>
              <input
                type="tel"
                className="form-control h-56-px bg-neutral-50 radius-12"
                style={{ paddingLeft: "48px" }}
                placeholder="Enter Phone Number"
                value={phoneNumber}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "");
                  if (value.length <= 10) {
                    setPhoneNumber(value);
                  }
                  setErrors((prev) => ({ ...prev, phoneNumber: "" }));
                }}
                maxLength={10}
                disabled={loading}
              />
            </div>
            {errors.phoneNumber && (
              <span className="text-danger text-xs mt-1">
                {errors.phoneNumber}
              </span>
            )}
          </div>

          <div className="mb-32 text-start">
            <label className="form-label text-secondary text-sm fw-medium mb-8">
              PIN
            </label>
            <div className="d-flex align-items-center gap-20">
              <div className="d-flex gap-12">
                {pin.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (pinRefs.current[index] = el)}
                    type={showPin ? "text" : "password"}
                    inputMode="numeric"
                    className="form-control h-56-px bg-neutral-50 radius-12 text-center text-lg fw-bold focus-border-primary p-0 pin-input"
                    style={{ width: "75px" }}
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handlePinChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    disabled={loading}
                  />
                ))}
              </div>
              <button
                type="button"
                className="btn btn-sm border-0 p-0 text-secondary shadow-none"
                onClick={() => setShowPin(!showPin)}
                tabIndex="-1"
              >
                <Icon
                  icon={showPin ? "lucide:eye-off" : "lucide:eye"}
                  className="text-2xl"
                />
              </button>
            </div>
            {errors.pin && (
              <span className="text-danger text-xs mt-1 d-block">{errors.pin}</span>
            )}
            {/* <div className="text-end mt-8">
              <Link
                to="/forgot-pin"
                className="text-primary-600 fw-semibold text-sm"
              >
                Forgot PIN?
              </Link>
            </div> */}
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-lg w-100 radius-12 h-56-px fw-semibold"

            disabled={loading}
          >
            {loading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Signing In...
              </>
            ) : (
              "Sign In"
            )}
          </button>
        </form>
      </div>
    </section>
  );
};

export default SignInLayer;
